import React from "react";
import { useState, useRef, useEffect } from "react";
import '../../styles/grievance/main.css';

const RECAPTCHA_SITE_KEY = "6LcXI1MtAAAAAKyOx9WKnsHL0pjQ8QVftCBPtr4B";

// Backend endpoint that receives the form data and sends the grievance email.
const SUBMIT_URL = "https://smtp.ccsuniversity.ac.in/send-form-data";

const MAX_SUBMISSIONS_PER_USER = 3;
const SUBMISSION_STORAGE_KEY = "grievanceSubmissionCounts";

const departments = [
  "Biotechnology",
  "Botany",
  "Charak School of Pharmacy",
  "Chemistry",
  "Commerce",
  "Computer Application",
  "Economics",
  "Education",
  "English",
  "Environmental Science",
  "Food Science & Technology",
  "Foreign Language",
  "Forensic Science",
  "Genetics & Plant Breeding",
  "Geography",
  "Hindi एवं आधुनिक भारतीय भाषा विभाग",
  "History",
  "Home Science",
  "Horticulture",
  "Institute of Business Studies",
  "Institute of Engineering & Technology",
  "Institute of Fine Arts",
  "Jewellery Design & Technology",
  "Legal Studies",
  "Library and Information Science",
  "Mathematics",
  "Microbiology",
  "Physical Education",
  "Physics",
  "Plant Protection",
  "Political Science",
  "Psychology",
  "Sanskrit",
  "Seed Science & Technology",
  "Sociology",
  "Statistics",
  "Tilak School of Journalism and Mass Communication",
  "Toxicology",
  "Urdu",
  "Yoga Science",
  "Zoology",
  "Any Other",
];

const courses = [
  "Ph.D.",
  "Masters/Post Graduate",
  "Bachelors/Graduate",
  "LLM",
  "LLB"
];

const grievanceCategory = [
  "Examination",
  "Marksheet",
  "Admission",
  "Degree",
  "RTI",
  "Affiliation",
  "Fees",
  "Any Other"
];

const states = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi (NCT)",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

const getSubmissionCounts = () => {
  try {
    return JSON.parse(localStorage.getItem(SUBMISSION_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const getSubmissionCountForEmail = (email) => {
  if (!email) return 0;
  const counts = getSubmissionCounts();
  return counts[email.trim().toLowerCase()] || 0;
};

const incrementSubmissionCountForEmail = (email) => {
  const counts = getSubmissionCounts();
  const key = email.trim().toLowerCase();
  counts[key] = (counts[key] || 0) + 1;
  localStorage.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify(counts));
};

const initialFormData = {
  // Personal Details
  name: "",
  email: "",
  mobile: "",
  gender: "",
  enrollment: "",
  department: "",

  otherDepartment: "",

  course: "",
  // Address
  address: "",
  state: "",
  pincode: "",

  // Grievance
  grievanceCategory: "",
  otherGrievanceCategory: "",
  grievanceDescription: "",
  uploadDocument: "No",
  document: null,
  proposedSolution: "",

  // Declaration
  declaration: false,
};

const Main = () => {

  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | "success" | "error"
  const [submitMessage, setSubmitMessage] = useState("");
  const fileInputRef = useRef(null);

  // Load the reCAPTCHA v3 script once, on mount.
  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // changeHandler Function
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: fieldValue,
      };

      if (name === "grievanceCategory" && value !== "Any Other") {
        updatedData.otherGrievanceCategory = "";
      }

      if (name === "department" && value !== "Any Other") {
        updatedData.otherDepartment = "";
      }

      // Clear uploaded file if user selects "No"
      if (name === "uploadDocument" && value === "No") {
        updatedData.document = null;
      }

      return updatedData;
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      ...(name === "uploadDocument" && value === "No"
        ? { document: "" }
        : {}),
    }));
  };

  // validation Function
  const validatePersonalDetails = () => {

    const newErrors = {};

    // Personal Details

    if (!formData.name.trim())
      newErrors.name = "Name is required";

    if (!formData.email.trim())
      newErrors.email = "Email is required";
    else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    )
      newErrors.email = "Invalid Email";

    if (!formData.mobile.trim())
      newErrors.mobile = "Mobile Number is required";
    else if (!/^[6-9]\d{9}$/.test(formData.mobile))
      newErrors.mobile = "Enter valid 10 digit mobile number";

    if (!formData.gender)
      newErrors.gender = "Select Gender";

    if (!formData.enrollment.trim())
      newErrors.enrollment = "Enrollment Number is required";

    if (!formData.department)
      newErrors.department = "Select Department";

    // other department
    if (
      formData.department === "Any Other" &&
      !formData.otherDepartment.trim()
    ) {
      newErrors.otherDepartment =
        "Please enter the department/college";
    }

    if (!formData.course)
      newErrors.course = "Select Course";

    // Address

    if (!formData.address.trim())
      newErrors.address = "Address is required";

    if (!formData.state.trim())
      newErrors.state = "State is required";

    if (!formData.pincode.trim())
      newErrors.pincode = "Pin Code is required";
    else if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Enter valid 6 digit Pin Code";

    // Grievance

    if (!formData.grievanceCategory)
      newErrors.grievanceCategory = "Select Grievance Category";

    if (
      formData.grievanceCategory === "Any Other" &&
      !formData.otherGrievanceCategory.trim()
    ) {
      newErrors.otherGrievanceCategory =
        "Please enter the grievance category";
    }

    if (!formData.grievanceDescription.trim())
      newErrors.grievanceDescription = "Please describe your grievance";

    if (
      formData.uploadDocument === "Yes" &&
      !formData.document
    ) {
      newErrors.document = "Please upload the document";
    }

    // Declaration

    if (!formData.declaration)
      newErrors.declaration = "Please accept the declaration";

    // Max submissions per user (email-based, client-side check)
    if (
      formData.email.trim() &&
      getSubmissionCountForEmail(formData.email) >= MAX_SUBMISSIONS_PER_USER
    ) {
      newErrors.email = `You have already submitted ${MAX_SUBMISSIONS_PER_USER} grievances with this email. Limit reached.`;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Runs the invisible reCAPTCHA v3 check and resolves with a token.
  const getCaptchaToken = () => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        reject(new Error("reCAPTCHA has not loaded yet"));
        return;
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: "grievance_submit" })
          .then(resolve)
          .catch(reject);
      });
    });
  };

  // Builds a multipart/form-data payload (required because of the file upload)
  // and sends it to the backend.
  const submitToBackend = async (captchaToken) => {

    const payload = new FormData();

    payload.append("name", formData.name);
    payload.append("email", formData.email);
    payload.append("mobile", formData.mobile);
    payload.append("gender", formData.gender);
    payload.append("enrollment", formData.enrollment);
    payload.append("department", formData.department);
    payload.append(
      "otherDepartment",
      formData.department === "Any Other"
      ? formData.otherDepartment
      : ""
    );
    payload.append("course", formData.course);
    payload.append("address", formData.address);
    payload.append("state", formData.state);
    payload.append("pincode", formData.pincode);
    payload.append("grievanceCategory", formData.grievanceCategory);
    payload.append(
      "otherGrievanceCategory",
      formData.grievanceCategory === "Any Other"
        ? formData.otherGrievanceCategory
        : ""
    );
    payload.append("grievanceDescription", formData.grievanceDescription);
    payload.append("proposedSolution", formData.proposedSolution);
    payload.append("declaration", formData.declaration ? "Yes" : "No");
    payload.append("captchaToken", captchaToken);

    if (formData.uploadDocument === "Yes" && formData.document) {
      payload.append("document", formData.document, formData.document.name);
    }

    const response = await fetch(SUBMIT_URL, {
      method: "POST",
      body: payload,
      // Do NOT set a Content-Type header manually — the browser sets the
      // correct multipart boundary automatically when body is a FormData.
    });

    if (!response.ok) {
      let serverMessage = "";
      try {
        const data = await response.json();
        serverMessage = data?.message || "";
      } catch {
        // response wasn't JSON, ignore
      }
      throw new Error(serverMessage || `Server responded with status ${response.status}`);
    }

    return response;
  };

  // submitHandler Function
  const handleSubmit = async (e) => {

    if (e && e.preventDefault) e.preventDefault();

    setSubmitStatus(null);
    setSubmitMessage("");

    const isValid = validatePersonalDetails();

    if (!isValid) {
      const firstErrorField = document.querySelector(".is-invalid");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSubmitting(true);

    let captchaToken;
    try {
      captchaToken = await getCaptchaToken();
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        captcha: "Could not verify reCAPTCHA. Please refresh the page and try again.",
      }));
      setSubmitting(false);
      return;
    }

    try {
      await submitToBackend(captchaToken);

      // Record this submission locally so the 3-submission limit is enforced
      // for this browser/email going forward.
      incrementSubmissionCountForEmail(formData.email);

      setSubmitStatus("success");
      setSubmitMessage("Your grievance has been submitted successfully. You will receive a confirmation email shortly.");

      // Reset the form for a fresh submission
      setFormData(initialFormData);
      setErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage(
        err?.message ||
        "Something went wrong while submitting your grievance. Please try again in a moment."
      );
    }

    setSubmitting(false);
  };

  // handleFileChange Function
  const handleFileChange = (e) => {

    const file = e.target.files[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {

      setErrors((prev) => ({
        ...prev,
        document: "Only PDF, PNG, JPG, JPEG, DOC & DOCX files are allowed.",
      }));

      e.target.value = "";

      return;
    }

    if (file.size > 500 * 1024) {

      setErrors((prev) => ({
        ...prev,
        document: "Maximum file size allowed is 500 KB.",
      }));

      e.target.value = "";

      return;
    }

    setFormData((prev) => ({
      ...prev,
      document: file,
    }));

    setErrors((prev) => ({
      ...prev,
      document: "",
    }));
  };

  return (
    <section className="grievance-form-section py-5">
      <div className="container">

        <div className="row justify-content-center">

          <div className="col-lg-10">

            <div className="card shadow-sm border-0">

              <div className="card-header text-center">

                <h2 className="mb-0">
                  Student Grievance Form
                </h2>

              </div>

              <div className="card-body">

                {
                  submitStatus && (
                    <div
                      className={`alert ${submitStatus === "success" ? "alert-success" : "alert-danger"} text-center`}
                      role="alert"
                    >
                      {submitMessage}
                    </div>
                  )
                }

                <form
                  className="grievance-form-layout"
                  onSubmit={handleSubmit}
                >

                  {/* Personal Details Section */}
                  <div className="row">
                    <div className="col-12">

                      <div className="mb-5">

                        <div className="section-heading">
                          Personal Details
                        </div>

                        <div className="row mt-4">

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Full Name <span className="text-danger">*</span>
                            </label>

                            <input
                              type="text"
                              name="name"
                              className={`form-control ${errors.name ? "is-invalid" : ""}`}
                              placeholder="Enter Full Name"
                              value={formData.name}
                              onChange={handleChange}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                              }}
                            />

                            <div className="invalid-feedback">
                              {errors.name}
                            </div>

                          </div>

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Email ID <span className="text-danger">*</span>
                            </label>

                            <input
                              type="email"
                              name="email"
                              className={`form-control ${errors.email ? "is-invalid" : ""}`}
                              placeholder="Enter Email"
                              value={formData.email}
                              onChange={handleChange}
                            />

                            <div className="invalid-feedback">
                              {errors.email}
                            </div>

                          </div>

                        </div>

                        <div className="row">

                          <div className="col-md-4 mb-3">

                            <label className="form-label">
                              Mobile Number <span className="text-danger">*</span>
                            </label>

                            <input
                              type="text"
                              name="mobile"
                              maxLength={10}
                              className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                              placeholder="Enter Mobile Number"
                              value={formData.mobile}
                              onChange={handleChange}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, "");
                              }}
                            />

                            <div className="invalid-feedback">
                              {errors.mobile}
                            </div>

                          </div>

                          <div className="col-md-4 mb-3">

                            <label className="form-label">
                              Gender <span className="text-danger">*</span>
                            </label>

                            <select
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                              className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                            >
                              <option value="">Select Gender</option>
                              <option>Male</option>
                              <option>Female</option>
                              <option>Other</option>
                            </select>

                            <div className="invalid-feedback">
                              {errors.gender}
                            </div>

                          </div>

                          <div className="col-md-4 mb-3">

                            <label className="form-label">
                              Enrollment Number <span className="text-danger">*</span>
                            </label>

                            <input
                              type="text"
                              name="enrollment"
                              value={formData.enrollment}
                              onChange={handleChange}
                              className={`form-control ${errors.enrollment ? "is-invalid" : ""}`}
                              placeholder="Enter Enrollment Number"
                            />

                            <div className="invalid-feedback">
                              {errors.enrollment}
                            </div>

                          </div>

                        </div>

                        <div className="row">

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Department <span className="text-danger">*</span>
                            </label>

                            <select
                              name="department"
                              value={formData.department}
                              onChange={handleChange}
                              className={`form-select ${errors.department ? "is-invalid" : ""}`}
                            >
                              <option value="">Select Department</option>

                              {departments.map((department) => (
                                <option
                                  key={department}
                                  value={department}
                                >
                                  {department}
                                </option>
                              ))}

                            </select>

                            <div className="invalid-feedback">
                              {errors.department}
                            </div>

                          </div>

                          {/* This is for Other Department or College */}
                          {formData.department === "Any Other" && (

                            <div className="col-md-6 mb-3">

                              <label className="form-label">
                                Other Department / College
                                <span className="text-danger">*</span>
                              </label>

                              <input
                                type="text"
                                name="otherDepartment"
                                value={formData.otherDepartment}
                                onChange={handleChange}
                                placeholder="Enter Department/College"
                                className={`form-control ${
                                  errors.otherDepartment ? "is-invalid" : ""
                                }`}
                              />

                              <div className="invalid-feedback">
                                {errors.otherDepartment}
                              </div>

                            </div>

                          )}

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Course <span className="text-danger">*</span>
                            </label>

                            <select
                              name="course"
                              value={formData.course}
                              onChange={handleChange}
                              className={`form-select ${errors.course ? "is-invalid" : ""}`}
                            >
                              <option value="">Select Course</option>

                              {courses.map((course) => (
                                <option
                                  key={course}
                                  value={course}
                                >
                                  {course}
                                </option>
                              ))}

                            </select>

                            <div className="invalid-feedback">
                              {errors.course}
                            </div>

                          </div>

                        </div>

                      </div>

                    </div>

                  </div>

                  {/* Adress Section */}
                  <div className="row">
                    <div className="col-12">

                      <div className="mb-5">

                        <div className="section-heading">
                          Permanent Address
                        </div>

                        <div className="row mt-4">

                          <div className="col-md-12 mb-3">

                            <label className="form-label">
                              Address <span className="text-danger">*</span>
                            </label>

                            <textarea
                              rows="3"
                              name="address"
                              className={`form-control ${errors.address ? "is-invalid" : ""}`}
                              value={formData.address}
                              onChange={handleChange}
                              placeholder="Enter Complete Address"
                            />

                            <div className="invalid-feedback">
                              {errors.address}
                            </div>

                          </div>

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              State <span className="text-danger">*</span>
                            </label>

                            <select
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              className={`form-select ${errors.state ? "is-invalid" : ""}`}
                            >
                              <option value="">Select State</option>

                              {states.map((state) => (
                                <option
                                  key={state}
                                  value={state}
                                >
                                  {state}
                                </option>
                              ))}
                            </select>

                            <div className="invalid-feedback">
                              {errors.state}
                            </div>

                          </div>

                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Pin Code <span className="text-danger">*</span>
                            </label>

                            <input
                              type="text"
                              maxLength="6"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleChange}
                              className={`form-control ${errors.pincode ? "is-invalid" : ""}`}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/\D/g, "");
                              }}
                            />

                            <div className="invalid-feedback">
                              {errors.pincode}
                            </div>

                          </div>

                        </div>

                      </div>

                    </div>
                  </div>

                  {/* Grievance Details Section */}
                  <div className="row">
                    <div className="col-12">

                      <div className="mb-5">

                        <div className="section-heading">
                          Grievance Details
                        </div>

                        <div className="row mt-4">

                          {/* Grievance Category */}
                          <div className="col-md-6 mb-3">

                            <label className="form-label">
                              Grievance Category <span className="text-danger">*</span>
                            </label>

                            <select
                              name="grievanceCategory"
                              value={formData.grievanceCategory}
                              onChange={handleChange}
                              className={`form-select ${errors.grievanceCategory ? "is-invalid" : ""}`}
                            >
                              <option value="">Select Category</option>

                              {grievanceCategory.map((category) => (
                                <option
                                  key={category}
                                  value={category}
                                >
                                  {category}
                                </option>
                              ))}

                            </select>

                            <div className="invalid-feedback">
                              {errors.grievanceCategory}
                            </div>

                          </div>

                          {/* Show only when "Any Other" is selected */}

                          {formData.grievanceCategory === "Any Other" && (

                            <div className="col-md-6 mb-3">

                              <label className="form-label">
                                Other Grievance Category
                                <span className="text-danger">*</span>
                              </label>

                              <input
                                type="text"
                                name="otherGrievanceCategory"
                                value={formData.otherGrievanceCategory}
                                onChange={handleChange}
                                placeholder="Enter Grievance Category"
                                className={`form-control ${
                                  errors.otherGrievanceCategory ? "is-invalid" : ""
                                }`}
                              />

                              <div className="invalid-feedback">
                                {errors.otherGrievanceCategory}
                              </div>

                            </div>

                          )}

                          <div className="col-md-12 mb-3">

                            <label className="form-label">
                              Grievance Description
                              <span className="text-danger">*</span>
                            </label>

                            <textarea
                              rows="5"
                              maxLength={150}
                              name="grievanceDescription"
                              value={formData.grievanceDescription}
                              onChange={handleChange}
                              className={`form-control ${errors.grievanceDescription ? "is-invalid" : ""}`}
                            />

                            <small className="text-muted">
                              {150 - formData.grievanceDescription.length} characters remaining
                            </small>

                            <div className="invalid-feedback">
                              {errors.grievanceDescription}
                            </div>

                          </div>

                          <div className="col-md-12 mb-4">

                            <label className="form-label">
                              Want to Upload Document?
                            </label>

                            <div>

                              <div className="form-check form-check-inline">

                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="uploadDocument"
                                  value="Yes"
                                  checked={formData.uploadDocument === "Yes"}
                                  onChange={handleChange}
                                />

                                <label className="form-check-label">
                                  Yes
                                </label>

                              </div>

                              <div className="form-check form-check-inline">

                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="uploadDocument"
                                  value="No"
                                  checked={formData.uploadDocument === "No"}
                                  onChange={handleChange}
                                />

                                <label className="form-check-label">
                                  No
                                </label>

                              </div>

                            </div>

                          </div>

                          {
                            formData.uploadDocument === "Yes" && (

                              <div className="col-md-12 mb-4">

                                <label className="form-label">
                                  Upload Document
                                </label>

                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  className={`form-control ${errors.document ? "is-invalid" : ""}`}
                                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                  onChange={handleFileChange}
                                />

                                <div className="invalid-feedback">
                                  {errors.document}
                                </div>

                                <small className="text-muted">
                                  Accepted File Types:
                                  PDF, PNG, JPG, JPEG, DOC, DOCX
                                  <br />
                                  Maximum File Size: 500 KB
                                </small>

                                {
                                  formData.document && (

                                    <div className="selected-file mt-3">

                                      <div className="d-flex justify-content-between align-items-center">

                                        <div>

                                          <h6 className="mb-1">
                                            {formData.document.name}
                                          </h6>

                                          <small className="text-muted">

                                            {(formData.document.size / 1024).toFixed(2)} KB

                                          </small>

                                        </div>

                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() => {
                                            setFormData((prev) => ({
                                              ...prev,
                                              document: null,
                                            }));

                                            if (fileInputRef.current) {
                                              fileInputRef.current.value = "";
                                            }

                                          }}
                                        >
                                          Remove
                                        </button>

                                      </div>

                                    </div>

                                  )
                                }

                              </div>

                            )
                          }

                          <div className="col-md-12 mb-3">

                            <label className="form-label">
                              Proposed Solution (Optional)
                            </label>

                            <textarea
                              rows="3"
                              maxLength={50}
                              name="proposedSolution"
                              value={formData.proposedSolution}
                              onChange={handleChange}
                              className="form-control"
                            />

                            <small className="text-muted">
                              {50 - formData.proposedSolution.length} characters remaining
                            </small>

                          </div>

                        </div>

                      </div>

                    </div>
                  </div>

                  {/* Declaration */}
                  <div className="row">
                    <div className="col-12">
                      <div className="mb-5">

                        <div className="section-heading">
                          Declaration
                        </div>

                        <div className="mt-4">

                          <div className="form-check">

                            <input
                              type="checkbox"
                              className={`form-check-input ${errors.declaration ? "is-invalid" : ""}`}
                              id="declaration"
                              name="declaration"
                              checked={formData.declaration}
                              onChange={handleChange}
                            />

                            <label
                              className="form-check-label"
                              htmlFor="declaration"
                            >
                              I hereby declare that the information/document provided above is correct.
                              I shall be responsible for furnishing any wrong information/document.
                            </label>

                            <div className="invalid-feedback">
                              {errors.declaration}
                            </div>

                          </div>

                        </div>

                      </div>
                    </div>
                  </div>

                  {
                    errors.captcha && (
                      <div className="text-danger text-center mb-3" style={{ fontSize: "0.9em" }}>
                        {errors.captcha}
                      </div>
                    )
                  }

                  {/* Submit button */}
                  <div className="text-center mt-3">
                    <button
                      type="submit"
                      className="grievance-submit-btn"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>

                    <p className="text-muted mt-2" style={{ fontSize: "0.75em" }}>
                      This site is protected by reCAPTCHA and the Google{" "}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{" "}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a> apply.
                    </p>
                  </div>
                  {
                    submitStatus && (
                      <div
                        className={`alert ${
                          submitStatus === "success"
                            ? "alert-success"
                            : "alert-danger"
                        } text-center`}
                        role="alert"
                      >
                        {submitMessage}
                      </div>
                    )
                  }
                </form>
              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  )
};

export default Main;